import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Delete, Warehouse, Loader2 } from "lucide-react";
import { setWarehouseSession, clearWarehouseSession } from "@/lib/warehouseSession";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MobileLogin() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [step, setStep] = useState("pin"); // pin | employee
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasPin, setHasPin] = useState(true);

  useEffect(() => {
    clearWarehouseSession();
    axios.get(`${API}/warehouse/status`)
      .then((r) => setHasPin(!!r.data.has_pin))
      .catch(() => {});
  }, []);

  const type = (d) => setPin((p) => (p.length >= 8 ? p : p + d));
  const clear = () => setPin("");
  const back = () => setPin((p) => p.slice(0, -1));

  const submitPin = async () => {
    if (pin.length < 4) {
      toast.error("Enter at least 4 digits");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/warehouse/verify-pin`, { pin });
      setEmployees(r.data.employees || []);
      if (!r.data.employees?.length) {
        toast.error("No active employees on file. Ask the manager to add staff first.");
        setPin("");
        return;
      }
      setStep("employee");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const chooseEmployee = (emp) => {
    setSelected(emp);
    setWarehouseSession({ employee: emp.name, employeeId: emp.id });
    toast.success(`Welcome ${emp.name}`);
    navigate("/mobile", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center mb-3">
            <Warehouse className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Warehouse Mobile</h1>
          <p className="text-sm text-slate-300 mt-1">
            {step === "pin" ? "Enter the shared PIN" : "Who are you?"}
          </p>
        </div>

        {!hasPin && step === "pin" && (
          <Card className="mb-4 bg-amber-100 border-amber-300">
            <CardContent className="p-3 text-xs text-amber-900">
              No PIN configured yet. Ask the manager to set one in
              <b> Settings → Security → Warehouse PIN</b>.
            </CardContent>
          </Card>
        )}

        {step === "pin" && (
          <Card className="bg-white/95 backdrop-blur">
            <CardContent className="p-4 space-y-4">
              <div
                className="h-14 rounded-xl bg-slate-100 flex items-center justify-center text-3xl tracking-[0.5em] font-mono font-bold text-slate-900"
                data-testid="mobile-pin-display"
              >
                {pin.replace(/./g, "•").padEnd(4, "○")}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <Button
                    key={d}
                    variant="outline"
                    className="h-14 text-xl font-bold"
                    onClick={() => type(String(d))}
                    data-testid={`mobile-pin-key-${d}`}
                  >
                    {d}
                  </Button>
                ))}
                <Button variant="ghost" className="h-14 text-sm" onClick={clear}>
                  Clear
                </Button>
                <Button
                  variant="outline"
                  className="h-14 text-xl font-bold"
                  onClick={() => type("0")}
                  data-testid="mobile-pin-key-0"
                >
                  0
                </Button>
                <Button variant="ghost" className="h-14" onClick={back}>
                  <Delete className="h-5 w-5" />
                </Button>
              </div>

              <Button
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                disabled={busy || pin.length < 4}
                onClick={submitPin}
                data-testid="mobile-pin-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "employee" && (
          <Card className="bg-white/95 backdrop-blur">
            <CardContent className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
              {employees.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => chooseEmployee(emp)}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:bg-amber-50 hover:border-amber-400 active:bg-amber-100 flex items-center justify-between transition"
                  data-testid={`mobile-employee-${emp.id}`}
                >
                  <span className="font-medium text-slate-900">{emp.name}</span>
                  <span className="text-xs text-slate-500">{emp.role || ""}</span>
                </button>
              ))}
              <Button
                variant="ghost"
                className="w-full mt-2"
                onClick={() => { setStep("pin"); setPin(""); }}
                data-testid="mobile-employee-back"
              >
                ← Back
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
